import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CustomAttributeFieldType,
  isCustomAttributeFieldType,
} from '@risksmart-app/form-configuration/src/field-types/types';
import type { FC, ReactElement, ReactNode, Ref } from 'react';
import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Control, FieldValues, Resolver } from 'react-hook-form';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import type {
  FormEditorPreviewData,
  FormPreviewHandle,
} from 'src/blocks/form-editor-dialog';
import { supportsDefaultValue } from 'src/blocks/form-editor-dialog/config';
import { useModulesStore } from 'src/context/moduleContext';
import { z, type ZodSchema } from 'zod';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { AdditionalConditionField } from '../edit-field-modal/formRegistryService';
import { ConditionalFieldsProvider } from './conditional-fields-provider/ConditionalFieldsProvider';
import { convertEditorStateToFormData } from './convertEditorStateToFormData';
import { createNewFieldElement } from './createNewFieldElement';
import { useTransformSchemaToRequiredFields } from './customisable-form/hooks/useTransformSchemaToRequiredFields';
import { useRiskSmartForm } from './customisable-form/RiskSmartFormContext';
import { RiskSmartFormProvider } from './customisable-form/RiskSmartFormProvider';
import { SectionedFormLayout } from './customisable-form/SectionedFormLayout';
import {
  CustomisableFormDataContext,
  useCustomisableFormDataContext,
} from './customisable-form-data/CustomisableFormDataContext';
import { FormPreviewContext } from './form-preview-provider/FormPreviewContext';
import {
  type FieldElement,
  useFormFieldElementsStore,
} from './stores/useFormFieldElementsStore';

const noOpAsync = async () => {
  /* preview never saves */
};

// `useTransformSchemaToRequiredFields` is normally invoked from
// `CustomisableFieldWrapper` on the host page — the preview reuses pre-rendered
// field elements from the store, so the hook never runs there and our resolver
// would stay at the identity wrapper (→ trigger always passes). Mount this
// installer inside the preview's providers so the hook DOES run here and
// populates `customFormValidation` with the real required-field wrapper.
type RequiredFieldsParentType = Parameters<
  typeof useTransformSchemaToRequiredFields
>[0];
const PreviewRequiredFieldsInstaller: FC<{
  parentType: RequiredFieldsParentType;
}> = ({ parentType }) => {
  useTransformSchemaToRequiredFields(parentType);

  return null;
};

const identityValidation = (schema: ZodSchema) => schema;
// `useTransformSchemaToRequiredFields` wraps whatever schema we hand it with
// the required-field checks for every Controller mounted in the preview tree.
// We don't have the entity's full zod schema in scope (URL/format rules are
// owned by the host page form), so we feed it a passthrough — required-field
// validation works correctly; format-only errors are not surfaced.
const previewBaseSchema = z.object({}).passthrough();

const overrideControlInTree = (
  node: ReactNode,
  control: Control<FieldValues>
): ReactNode => {
  if (Array.isArray(node)) {
    return node.map((c) => overrideControlInTree(c, control));
  }

  if (!isValidElement(node)) {
    return node;
  }

  const element = node as ReactElement<Record<string, unknown>>;
  const props = element.props;
  const hasControlProp = 'control' in props;
  const childrenProp = props.children;
  const hasChildren =
    childrenProp !== undefined &&
    childrenProp !== null &&
    typeof childrenProp !== 'function';

  if (!hasControlProp && !hasChildren) {
    return element;
  }

  const nextChildren = hasChildren
    ? Children.map(childrenProp as ReactNode, (c) =>
        overrideControlInTree(c, control)
      )
    : undefined;

  const nextProps: Record<string, unknown> = hasControlProp ? { control } : {};

  return cloneElement(
    element,
    Object.keys(nextProps).length > 0 ? nextProps : undefined,
    hasChildren ? nextChildren : undefined
  );
};

const PreviewFieldRenderer: FC<{ data: FormEditorPreviewData }> = ({
  data,
}) => {
  const existingContext = useCustomisableFormDataContext();
  const { parentType } = useRiskSmartForm();
  const { control } = useFormContext();
  const storedElements = useFormFieldElementsStore((s) => s.elements);
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  const modules = useModulesStore((s) => s.modules);

  const overriddenContext = useMemo(() => {
    if (!parentType) {
      return existingContext;
    }

    return convertEditorStateToFormData(
      data,
      parentType,
      existingContext,
      formSectionsEnabled,
      modules
    );
  }, [data, parentType, existingContext, formSectionsEnabled, modules]);

  const fieldElements = useMemo(() => {
    const storedByKey = new Map(storedElements.map((el) => [el.key, el]));
    const allFieldIds = data.containerOrder.flatMap(
      (id) => data.containers[id] ?? []
    );

    const elements = allFieldIds
      .map((fieldId) => {
        const field = data.fields[fieldId];
        if (!field) {
          return null;
        }

        const editorConfig = data.fieldConfigs[fieldId];

        if (editorConfig) {
          const regenerated = createNewFieldElement(
            fieldId,
            field,
            editorConfig,
            control
          );
          if (regenerated) {
            return regenerated;
          }
        }

        const stored = storedByKey.get(fieldId);
        if (stored) {
          return {
            key: stored.key,
            value: overrideControlInTree(stored.value, control) as ReactElement<
              { condition?: unknown } & Record<string, unknown>
            >,
          };
        }

        return null;
      })
      .filter((el): el is NonNullable<typeof el> => el !== null);

    return elements as FieldElement[];
  }, [storedElements, data, control]);

  const sections = useMemo(
    () =>
      data.containerOrder.map((id) => ({
        SectionId: id,
        Label: data.sectionNames[id] ?? id,
        Description: data.sectionConfigs?.[id]?.description ?? '',
        fields: data.containers[id] ?? [],
      })),
    [data]
  );

  // Fields created (or edited) in this editor session render via
  // createNewFieldElement at the top-level temp id rather than under the
  // persisted custom-attribute schema, so condition evaluation can't resolve
  // them from the standard/custom filtering properties. Surface them as
  // additional condition sources keyed by that same temp id. Existing custom
  // fields (scope-keyed) are already covered by the persisted schema.
  const additionalConditionFields = useMemo<AdditionalConditionField[]>(
    () =>
      Object.entries(data.fields)
        .filter(
          ([id, field]) =>
            isCustomAttributeFieldType(field.type) &&
            !id.startsWith('#/properties/') &&
            data.fieldConfigs[id] != null
        )
        .map(([id, field]) => {
          const config = data.fieldConfigs[id];

          return {
            key: id,
            type: field.type as CustomAttributeFieldType,
            label: config?.fieldName ?? field.name,
            options: config?.options,
          };
        }),
    [data]
  );

  const noOp = useMemo(
    () => () => {
      /* noop */
    },
    []
  );

  return (
    // Marks the subtree as the form editor's preview so field guidance renders
    // no info button here — the help sidebar can't open inside the dialog and
    // its popover fallback is clipped by it (CUS-201).
    <FormPreviewContext.Provider value={true}>
      <CustomisableFormDataContext.Provider value={overriddenContext}>
        <ConditionalFieldsProvider
          additionalConditionFields={additionalConditionFields}
        >
          {parentType ? (
            <PreviewRequiredFieldsInstaller parentType={parentType} />
          ) : null}
          <SectionedFormLayout
            sections={sections}
            draggedFieldId={null}
            handleFieldReorderInSection={noOp as never}
            setDraggedFieldId={noOp as never}
            fieldComponents={fieldElements}
            isEditMode={false}
          />
        </ConditionalFieldsProvider>
      </CustomisableFormDataContext.Provider>
    </FormPreviewContext.Provider>
  );
};

interface LiveFormPreviewProps {
  data: FormEditorPreviewData;
  /**
   * Called whenever the user edits a previewed field, so the dialog can reset
   * the "Test validation" button back to its neutral state.
   */
  onDataChange?: () => void;
  ref?: Ref<FormPreviewHandle>;
}

export const LiveFormPreview = ({
  data,
  onDataChange,
  ref,
}: LiveFormPreviewProps) => {
  const { parentType } = useRiskSmartForm();
  const parentForm = useFormContext();

  const initialValuesRef = useRef<FieldValues | null>(null);
  if (initialValuesRef.current === null) {
    const parentValues = parentForm.getValues();
    const seeded: FieldValues = { ...parentValues };

    const isEmptyValue = (value: unknown): boolean =>
      value === undefined || value === null || value === '';

    // The regenerated preview field binds to `name={fieldId}` (editor-id space,
    // see createNewFieldElement), but an existing record's value lives under the
    // form-value key, which can differ from the editor id — standard fields by
    // casing, custom fields as `CustomAttributeData.<path>` vs `#/properties/<path>`
    // (see convertEditorStateToFormData's remapPreviewPropertyKey). Resolve the
    // underlying value across those spaces so a default never clobbers a value the
    // record already holds.
    const resolveExistingValue = (fieldId: string): unknown => {
      if (!isEmptyValue(parentValues[fieldId])) {
        return parentValues[fieldId];
      }
      if (fieldId.startsWith('#/properties/')) {
        const path = fieldId.slice('#/properties/'.length);
        const customData = parentValues.CustomAttributeData as
          | Record<string, unknown>
          | undefined;
        if (customData && !isEmptyValue(customData[path])) {
          return customData[path];
        }
      }
      const lowerId = fieldId.toLowerCase();
      for (const [key, value] of Object.entries(parentValues)) {
        if (key.toLowerCase() === lowerId && !isEmptyValue(value)) {
          return value;
        }
      }

      return undefined;
    };

    // INVARIANT: inside the preview, both planes are keyed by the **editor id** —
    // the rendered element's RHF `name` (createNewFieldElement) and the config
    // rows `convertEditorStateToFormData` builds (`FieldId: fieldId`, deliberately
    // un-canonicalised). Only condition `propertyKey`s are translated into the
    // form-value key space. Any value the preview needs therefore has to be
    // mirrored onto the editor id here; adding a field type without mirroring
    // reproduces CUS-105 (default never appeared) or CUS-364 (record value lost).
    //
    // Rebinding the element to the form-value key instead was assessed in CUS-365
    // and rejected: `getRequiredFields` pairs the Controller-registered `name`
    // with the config row's `FieldId` case-insensitively, which bridges casing
    // (`description`/`Description`) but not shape — an element renamed to
    // `CustomAttributeData.x` stops matching its `#/properties/x` config row and
    // required-field validation silently disappears from the preview. Moving one
    // plane means moving both; see CUS-365 for the write-up.
    //
    // Mirror the record's value across first; only then fall back to an
    // in-session default (absent from the host form because it isn't persisted
    // yet), matching FormContext's fill-if-empty semantics.
    for (const fieldId of Object.keys(data.fieldConfigs)) {
      const config = data.fieldConfigs[fieldId];
      const field = data.fields[fieldId];
      if (!field) {
        continue;
      }

      const existing = resolveExistingValue(fieldId);
      if (!isEmptyValue(existing)) {
        seeded[fieldId] = existing;
        continue;
      }

      if (
        config?.defaultValueEnabled &&
        config.defaultValue &&
        supportsDefaultValue(field.type) &&
        isEmptyValue(seeded[fieldId])
      ) {
        seeded[fieldId] = config.defaultValue;
      }
    }
    initialValuesRef.current = seeded;
  }

  // `useTransformSchemaToRequiredFields` (running inside the preview's field
  // tree) installs the required-field wrapper into this state. Hold it in a
  // ref so the resolver picks up the latest wrapper on every validation run —
  // useForm captures its resolver at first render and doesn't re-read it.
  const [customFormValidation, setCustomFormValidation] = useState<
    (schema: ZodSchema) => ZodSchema
  >(() => identityValidation);
  const customFormValidationRef = useRef(customFormValidation);
  customFormValidationRef.current = customFormValidation;

  const resolverRef = useRef<Resolver<FieldValues> | null>(null);
  if (resolverRef.current === null) {
    resolverRef.current = (values, context, options) =>
      zodResolver(customFormValidationRef.current(previewBaseSchema))(
        values,
        context,
        options
      );
  }

  const previewMethods = useForm<FieldValues>({
    defaultValues: initialValuesRef.current,
    resolver: resolverRef.current,
  });

  // The preview never saves, but `Controller` and `useTransformSchemaToRequiredFields`
  // call these setters — wire real `useState` so they're proper Dispatch fns.
  const [, setOnSave] = useState<(() => Promise<void>) | undefined>(undefined);
  const [, setBeforeSaveHooks] = useState<(() => Promise<boolean>)[]>([]);
  const [, setAfterSaveHooks] = useState<
    ((savedId: string | undefined) => Promise<void>)[]
  >([]);

  const validate = useCallback(
    async () => previewMethods.trigger(),
    [previewMethods]
  );

  useImperativeHandle(ref, () => ({ validate }), [validate]);

  // Reset the dialog's "Test validation" button when the user edits a field.
  useEffect(() => {
    if (!onDataChange) {
      return;
    }
    const subscription = previewMethods.watch(() => onDataChange());

    return () => subscription.unsubscribe();
  }, [previewMethods, onDataChange]);

  return (
    <RiskSmartFormProvider
      parentType={parentType ?? undefined}
      onSave={undefined}
      setOnSave={setOnSave}
      setCustomFormValidation={setCustomFormValidation}
      setBeforeSaveHooks={setBeforeSaveHooks}
      beforeSaveHooks={[]}
      setAfterSaveHooks={setAfterSaveHooks}
      afterSaveHooks={[]}
      previewChanges={null}
      readOnly={false}
      defaultOnSave={noOpAsync}
    >
      <FormProvider {...previewMethods}>
        <PreviewFieldRenderer data={data} />
      </FormProvider>
    </RiskSmartFormProvider>
  );
};
